/**
 * UK SSP Cost Modeller - Main Application
 * Compare current SSP rules vs April 2026 Employment Rights Act changes
 */

import React, { useState, useMemo, useCallback } from 'react';
import { 
  Calculator, 
  Users, 
  TrendingUp, 
  FileSpreadsheet, 
  Download, 
  Save, 
  FolderOpen,
  RefreshCw,
  Info,
  ChevronDown,
  Building2,
  Briefcase,
} from 'lucide-react';

// Components
import Hero from './components/Hero';
import IndividualCalculator from './components/IndividualCalculator';
import WorkforceModeller from './components/WorkforceModeller';
import AWEScenarios from './components/AWEScenarios';
import ScenarioManager from './components/ScenarioManager';
import Header from './components/Header';
import RegulatoryExplainer from './components/RegulatoryExplainer';
import WorkedExamples from './components/WorkedExamples';

// Hooks
import { useScenarios, useCohorts, useSicknessPattern, useBackfillOptions } from './hooks/useSSPState';

// Utils
import { calculateCohortCosts, aggregateCohortResults } from './utils/sspCalculations';
import { exportToExcel, exportToPDF } from './utils/exportUtils';

// Constants
import { SCENARIO_TEMPLATES } from './data/constants';

function App() {
  // Tab state
  const [activeTab, setActiveTab] = useState('workforce');
  const [showScenarioManager, setShowScenarioManager] = useState(false);
  const [showHero, setShowHero] = useState(true);
  
  // Scenario management
  const {
    scenarios,
    currentScenarioId,
    saveScenario,
    loadScenario,
    deleteScenario,
    duplicateScenario,
  } = useScenarios();
  
  // Workforce state
  const { cohorts, setCohorts, addCohort, updateCohort, removeCohort, resetCohorts } = useCohorts();
  const { pattern, setPattern, updateDistribution, updateAvgLength, updateEpisodes, distributionTotal, isValidDistribution } = useSicknessPattern();
  const { options: backfillOptions, updateOption: updateBackfillOption } = useBackfillOptions();
  
  // Scenario name for exports
  const [scenarioName, setScenarioName] = useState('Hospitality Workforce');
  
  // Calculate workforce results
  const workforceResults = useMemo(() => {
    const sicknessPattern = {
      avgEpisodesPerEmployee: pattern.avgEpisodesPerEmployee,
      distribution: pattern.distribution,
      avgLengths: pattern.avgLengths,
    };
    
    const cohortResults = cohorts.map(cohort => 
      calculateCohortCosts(cohort, sicknessPattern, backfillOptions)
    );
    
    const totals = aggregateCohortResults(cohortResults);
    
    return {
      cohortResults,
      totals,
      sicknessPattern,
      options: backfillOptions,
    };
  }, [cohorts, pattern, backfillOptions]);
  
  // Handle scenario save
  const handleSaveScenario = useCallback((name) => {
    const data = {
      cohorts,
      pattern,
      backfillOptions,
      scenarioName: name || scenarioName,
    };
    saveScenario(name || scenarioName, data);
    setScenarioName(name || scenarioName);
  }, [cohorts, pattern, backfillOptions, scenarioName, saveScenario]);
  
  // Handle scenario load
  const handleLoadScenario = useCallback((id) => {
    const data = loadScenario(id);
    if (data) {
      setCohorts(data.cohorts);
      setPattern(data.pattern);
      setScenarioName(data.scenarioName || 'Loaded Scenario');
    }
    setShowScenarioManager(false);
  }, [loadScenario, setCohorts, setPattern]);
  
  // Handle template load
  const handleLoadTemplate = useCallback((templateKey) => {
    const template = SCENARIO_TEMPLATES[templateKey];
    if (template) {
      setCohorts(template.cohorts.map((c, i) => ({ ...c, id: `${templateKey}-${i}` })));
      setPattern(template.sicknessPattern);
      setScenarioName(template.name);
    }
  }, [setCohorts, setPattern]);
  
  // Handle export
  const handleExport = useCallback((format) => {
    const exportData = {
      ...workforceResults,
      scenarioName,
    };
    
    if (format === 'excel') {
      exportToExcel(exportData, scenarioName);
    } else if (format === 'pdf') {
      exportToPDF(exportData, scenarioName);
    }
  }, [workforceResults, scenarioName]);
  
  const tabs = [
    { id: 'workforce', label: 'Workforce Modelling', icon: Users },
    { id: 'individual', label: 'Individual Calculator', icon: Calculator },
    { id: 'examples', label: 'Worked Examples', icon: FileSpreadsheet },
    { id: 'explainer', label: 'How SSP Works', icon: Info },
    { id: 'awe', label: '8-Week AWE Scenarios', icon: TrendingUp },
  ];
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        scenarioName={scenarioName}
        onScenarioNameChange={setScenarioName}
        onSave={() => handleSaveScenario()}
        onExportExcel={() => handleExport('excel')}
        onExportPDF={() => handleExport('pdf')}
        onOpenScenarios={() => setShowScenarioManager(true)}
      />

      {/* Hero Section */}
      {showHero && (
        <Hero onGetStarted={() => setShowHero(false)} />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-1 p-1 bg-slate-200/60 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* Template selector (workforce tab only) */}
          {activeTab === 'workforce' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Quick start:</span>
              <div className="relative">
                <select
                  onChange={(e) => e.target.value && handleLoadTemplate(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-sm cursor-pointer hover:border-slate-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Load template...</option>
                  {Object.entries(SCENARIO_TEMPLATES).map(([key, template]) => (
                    <option key={key} value={key}>{template.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>
        
        {/* Tab Content */}
        <div className="animate-fade-in">
          {activeTab === 'workforce' && (
            <WorkforceModeller
              cohorts={cohorts}
              onAddCohort={addCohort}
              onUpdateCohort={updateCohort}
              onRemoveCohort={removeCohort}
              onResetCohorts={resetCohorts}
              pattern={pattern}
              onUpdateDistribution={updateDistribution}
              onUpdateAvgLength={updateAvgLength}
              onUpdateEpisodes={updateEpisodes}
              distributionTotal={distributionTotal}
              isValidDistribution={isValidDistribution}
              backfillOptions={backfillOptions}
              onUpdateBackfillOption={updateBackfillOption}
              results={workforceResults}
            />
          )}
          
          {activeTab === 'individual' && (
            <IndividualCalculator />
          )}

          {activeTab === 'examples' && (
            <WorkedExamples />
          )}

          {activeTab === 'explainer' && (
            <RegulatoryExplainer />
          )}

          {activeTab === 'awe' && (
            <AWEScenarios />
          )}
        </div>
      </main>
      
      {/* Scenario Manager Modal */}
      {showScenarioManager && (
        <ScenarioManager
          scenarios={scenarios}
          currentScenarioId={currentScenarioId}
          onLoad={handleLoadScenario}
          onDelete={deleteScenario}
          onDuplicate={duplicateScenario}
          onClose={() => setShowScenarioManager(false)}
          onSaveNew={handleSaveScenario}
        />
      )}
      
      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center gap-6 text-center">
            {/* Made with love */}
            <div className="flex items-center gap-2 text-slate-700">
              <span className="text-red-500 text-lg">♥</span>
              <span className="font-medium">Made with love to help UK employers prepare for 2026 SSP changes</span>
            </div>

            {/* Feedback */}
            <div className="text-sm text-slate-600">
              <span>Have feedback or suggestions? </span>
              <a
                href="mailto:feedback@sspcalculator.uk"
                className="text-primary-600 hover:text-primary-700 font-medium underline"
              >
                feedback@sspcalculator.uk
              </a>
            </div>

            {/* Crypto donations */}
            <div className="bg-slate-50 rounded-lg p-4 max-w-2xl w-full border border-slate-200">
              <p className="text-sm text-slate-700 font-medium mb-3">
                Found this useful? Support development with crypto donations:
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between gap-2 bg-white p-2 rounded border border-slate-200">
                  <span className="font-medium text-slate-600 min-w-[60px]">BTC:</span>
                  <code className="flex-1 text-slate-800 font-mono bg-slate-50 px-2 py-1 rounded text-[10px] break-all">
                    bc1q5kes5d0qhtt4tspyv4phe88jnd6w0eg52tc95x
                  </code>
                </div>
                <div className="flex items-center justify-between gap-2 bg-white p-2 rounded border border-slate-200">
                  <span className="font-medium text-slate-600 min-w-[60px]">ETH/USDT:</span>
                  <code className="flex-1 text-slate-800 font-mono bg-slate-50 px-2 py-1 rounded text-[10px] break-all">
                    0x2b342beafb8eFE8f2e92e24216A8E494AD10BcB4
                  </code>
                </div>
                <div className="flex items-center justify-between gap-2 bg-white p-2 rounded border border-slate-200">
                  <span className="font-medium text-slate-600 min-w-[60px]">Solana:</span>
                  <code className="flex-1 text-slate-800 font-mono bg-slate-50 px-2 py-1 rounded text-[10px] break-all">
                    2tWUwksq8jgTE9KM8B1uFJGWs2NcNvP4eJLcSCChZfZF
                  </code>
                </div>
              </div>
            </div>

            {/* Legal disclaimer */}
            <div className="text-xs text-slate-400 max-w-3xl">
              Based on Employment Rights Act 2024. SSP calculations are estimates only.
              Always verify with HMRC guidance or professional advice for actual payroll.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
